import React from 'react';
import { Player, PlayerStats } from '../../../types';
import { useResponsive } from '../../../hooks/useResponsive';
import { Tooltip } from 'react-tooltip';
import { PlayerAvatar } from './PlayerAvatar';
import { PlayerInfo } from './PlayerInfo';
import { PlayerBadges } from './PlayerBadges';
import { PlayerStats as PlayerStatsComponent } from './PlayerStats';
import { PlayerActions } from './PlayerActions';
import { PlayerReviews } from './PlayerReviews';
import { PlayerOrganizerPanel } from './PlayerOrganizerPanel';
import { createBadges, getCardStyle } from './PlayerCardStyles';

interface PlayerCardProps {
  player: Player;
  userIsCreator: boolean;
  onRemovePlayer: (playerId: string) => void;
  onSelfRemove?: (playerId: string) => void;
  onEditPlayer?: (player: Player) => void;
  onCreateReview?: (player: Player) => void;
  onViewReviews?: (player: Player) => void;
  onViewProfile?: (player: Player) => void;
  onValidateStats?: (player: Player) => void;
  currentUserId?: string;
  playerStats?: PlayerStats;
  matchStatus?: string;
  isMVP?: boolean;
  isTopScorer?: boolean;
  isTopAssister?: boolean;
  isTopSaver?: boolean;
  isMatchCreator?: boolean;
  // Props para drag & drop
  isDragging?: boolean;
  isDragOver?: boolean;
  // Props para reviews
  hasReviewedPlayer?: boolean;
  canViewReviews?: boolean;
  loadingReviews?: boolean;
  // Props para validación
  hasPendingStats?: boolean;
  // Props para organizer panel
  allPlayers?: Player[];
  onSetPlayerSkill?: (playerId: string, score: number) => void;
  onLinkFriends?: (playerAId: string, playerBId: string) => void;
  onUnlinkFriend?: (playerId: string) => void;
  onGenerateReplacementLink?: (player: Player) => void;
}

export const PlayerCard: React.FC<PlayerCardProps> = ({
  player,
  userIsCreator,
  onRemovePlayer,
  onSelfRemove,
  onEditPlayer,
  onCreateReview,
  onViewReviews,
  onViewProfile,
  onValidateStats,
  currentUserId,
  playerStats,
  matchStatus,
  isMVP,
  isTopScorer,
  isTopAssister,
  isTopSaver,
  isMatchCreator,
  isDragging,
  isDragOver,
  hasReviewedPlayer,
  canViewReviews,
  loadingReviews,
  hasPendingStats,
  allPlayers,
  onSetPlayerSkill,
  onLinkFriends,
  onUnlinkFriend,
  onGenerateReplacementLink,
}) => {
  const { isMobile, isCompact } = useResponsive();
  const showCreatorBadge = isMatchCreator && matchStatus !== 'FINISHED';
  const shouldShowStats = matchStatus === 'FINISHED';
  const safeStats = playerStats || { 
    playerId: player.id, 
    name: player.name, 
    goals: 0, 
    assists: 0, 
    saves: 0, 
    votes: 0, 
    votePercentage: 0 
  };

  // Crear badges
  const badges = createBadges(
    showCreatorBadge || false,
    isMVP || false,
    isTopScorer || false,
    isTopAssister || false,
    isTopSaver || false,
    safeStats
  );

  // Obtener estilos de la tarjeta
  const cardStyle = getCardStyle(
    showCreatorBadge || false,
    isMVP || false,
    isTopScorer || false,
    isTopAssister || false,
    isTopSaver || false
  );

  const hasSpecialBadge = isMVP || isTopScorer || isTopAssister || isTopSaver || showCreatorBadge;
  const textColor = '#FFFFFF';
  const secondaryTextColor = hasSpecialBadge ? '#E0E0E0' : '#B0BEC5';
  const statsTextColor = '#fff';

  return (
    <div
      onClick={() => {
        if (onViewProfile && player.userId) {
          onViewProfile(player);
        }
      }}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && onViewProfile && player.userId) onViewProfile(player); }}
      role={onViewProfile && player.userId ? 'button' : undefined}
      tabIndex={onViewProfile && player.userId ? 0 : undefined}
      data-tooltip-id={!player.userId ? `player-tooltip-${player.id}` : undefined}
      data-tooltip-content={!player.userId ? "Jugador no registrado - Sin perfil disponible" : undefined}
      style={{
        ...(isDragging ? { opacity: 0.3 } : {}),
        ...(isDragOver ? { border: '2px dashed #FFD600' } : {}),
        padding: isMobile || isCompact ? '12px' : '5px 10px',
        margin: isMobile || isCompact ? '8px 0' : '0',
        background: hasSpecialBadge ? cardStyle.background : '#23272A',
        borderRadius: isMobile || isCompact ? '12px' : '8px',
        boxShadow: hasSpecialBadge ? cardStyle.boxShadow : '0 2px 8px rgba(0,0,0,0.25)',
        border: hasSpecialBadge ? cardStyle.border : '1px solid #33383B',
        cursor: onViewProfile && player.userId ? 'pointer' : (userIsCreator ? 'grab' : 'default'),
        display: 'flex',
        alignItems: isMobile || isCompact ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        minHeight: isMobile || isCompact ? '44px' : '0',
        flex: isMobile || isCompact ? undefined : 1,
        position: 'relative',
        transition: 'background 0.2s, box-shadow 0.2s, border 0.2s',
        flexDirection: isMobile || isCompact ? 'column' : 'row',
        width: '100%',
        ...(onViewProfile && {
          '&:hover': {
            backgroundColor: hasSpecialBadge ? cardStyle.background : '#40444B',
            transform: 'scale(1.02)',
            boxShadow: hasSpecialBadge ? cardStyle.boxShadow : '0 4px 12px rgba(0, 230, 118, 0.2)'
          }
        })
      }}
    >
      {/* Sección izquierda: avatar, nombre, posición, badges */}
      <div style={{
        display: 'flex',
        alignItems: isMobile || isCompact ? 'flex-start' : 'center',
        gap: isMobile || isCompact ? '8px' : '8px',
        flex: 1,
        position: 'relative',
        width: '100%'
      }}>
        <PlayerAvatar
          player={player}
          isMobile={isMobile}
          isCompact={isCompact}
          showCreatorBadge={showCreatorBadge || false}
          isMVP={isMVP || false}
          isTopScorer={isTopScorer || false}
          isTopAssister={isTopAssister || false}
          isTopSaver={isTopSaver || false}
        />

        {/* Desktop: single row with info + badges inline */}
        {!(isMobile || isCompact) ? (
          <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
            <PlayerInfo
              player={player}
              isMobile={isMobile}
              isCompact={isCompact}
              textColor={textColor}
              secondaryTextColor={secondaryTextColor}
            />
            <PlayerBadges
              badges={badges}
              isMobile={isMobile}
              isCompact={isCompact}
            />
          </div>
        ) : (
          <div style={{ flex: 1, minWidth: 0 }}>
            <PlayerInfo
              player={player}
              isMobile={isMobile}
              isCompact={isCompact}
              textColor={textColor}
              secondaryTextColor={secondaryTextColor}
            />
            <PlayerBadges
              badges={badges}
              isMobile={isMobile}
              isCompact={isCompact}
            />
            {userIsCreator && matchStatus !== 'FINISHED' && onSetPlayerSkill && onLinkFriends && onUnlinkFriend && (
              <div style={{ marginTop: '6px' }}>
                <PlayerOrganizerPanel
                  player={player}
                  allPlayers={allPlayers || []}
                  onSetSkill={onSetPlayerSkill}
                  onLinkFriend={onLinkFriends}
                  onUnlinkFriend={onUnlinkFriend}
                />
              </div>
            )}
          </div>
        )}

        {/* Botones de acción para mobile/compact */}
        {(isMobile || isCompact) && (
          <PlayerActions
            player={player}
            userIsCreator={userIsCreator}
            currentUserId={currentUserId}
            matchStatus={matchStatus}
            isMatchCreator={isMatchCreator}
            isMobile={isMobile}
            isCompact={isCompact}
            onRemovePlayer={onRemovePlayer}
            onSelfRemove={onSelfRemove}
            onEditPlayer={onEditPlayer}
            onCreateReview={onCreateReview}
            onViewReviews={onViewReviews}
            onValidateStats={onValidateStats}
            hasReviewedPlayer={hasReviewedPlayer}
            canViewReviews={canViewReviews}
            loadingReviews={loadingReviews}
            hasPendingStats={hasPendingStats}
            onGenerateReplacementLink={onGenerateReplacementLink ? () => onGenerateReplacementLink(player) : undefined}
          />
        )}
      </div>

      {/* Sección derecha: stats y botones para desktop */}
      {!(isMobile || isCompact) && shouldShowStats && player.userId && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginLeft: '8px',
          justifyContent: 'flex-end',
          fontSize: '12px',
          flexShrink: 1,
          flexWrap: 'nowrap',
        }}>
          <PlayerStatsComponent
            stats={safeStats}
            hasPendingStats={hasPendingStats || false}
            statsTextColor={statsTextColor}
            isMobile={isMobile}
            isCompact={isCompact}
            onValidateStats={onValidateStats ? () => onValidateStats(player) : undefined}
            currentUserId={currentUserId}
            playerUserId={player.userId}
          />
          
          <PlayerReviews
            player={player}
            currentUserId={currentUserId}
            isMobile={isMobile}
            isCompact={isCompact}
            onCreateReview={onCreateReview}
            onViewReviews={onViewReviews}
            hasReviewedPlayer={hasReviewedPlayer}
            canViewReviews={canViewReviews}
            loadingReviews={loadingReviews}
          />
        </div>
      )}

      {/* Puntaje privado del organizador (desktop) */}
      {!(isMobile || isCompact) && userIsCreator && matchStatus !== 'FINISHED' && onSetPlayerSkill && onLinkFriends && onUnlinkFriend && (
        <div style={{ display: 'flex', alignItems: 'center', marginLeft: '8px', flexShrink: 0 }}>
          <PlayerOrganizerPanel
            player={player}
            allPlayers={allPlayers || []}
            onSetSkill={onSetPlayerSkill}
            onLinkFriend={onLinkFriends}
            onUnlinkFriend={onUnlinkFriend}
          />
        </div>
      )}

      {/* Botones de acción para desktop */}
      {!(isMobile || isCompact) && (
        <PlayerActions
          player={player}
          userIsCreator={userIsCreator}
          currentUserId={currentUserId}
          matchStatus={matchStatus}
          isMatchCreator={isMatchCreator}
          isMobile={isMobile}
          isCompact={isCompact}
          onRemovePlayer={onRemovePlayer}
          onSelfRemove={onSelfRemove}
          onEditPlayer={onEditPlayer}
          onCreateReview={onCreateReview}
          onViewReviews={onViewReviews}
          onValidateStats={onValidateStats}
          hasReviewedPlayer={hasReviewedPlayer}
          canViewReviews={canViewReviews}
          loadingReviews={loadingReviews}
          hasPendingStats={hasPendingStats}
          onGenerateReplacementLink={onGenerateReplacementLink ? () => onGenerateReplacementLink(player) : undefined}
        />
      )}

      {/* Stats para mobile/compact */}
      {(isMobile || isCompact) && shouldShowStats && player.userId && (
        <PlayerStatsComponent
          stats={safeStats}
          hasPendingStats={hasPendingStats || false}
          statsTextColor={statsTextColor}
          isMobile={isMobile}
          isCompact={isCompact}
          onValidateStats={onValidateStats ? () => onValidateStats(player) : undefined}
          currentUserId={currentUserId}
          playerUserId={player.userId}
        />
      )}

      {/* Indicador de stats pendientes para mobile/compact */}
      {(isMobile || isCompact) && shouldShowStats && player.userId && hasPendingStats && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 10px',
          backgroundColor: 'rgba(255, 152, 0, 0.15)',
          borderRadius: '8px',
          border: '1px solid rgba(255, 152, 0, 0.4)',
          marginTop: '8px',
          width: 'fit-content'
        }}>
          <span style={{fontSize: '16px', color: '#FF9800'}}>⚠️</span>
          <span style={{
            fontSize: '12px',
            color: '#FF9800',
            fontWeight: '600',
            whiteSpace: 'nowrap'
          }}>Pendiente de validación</span>
        </div>
      )}

      {/* Reviews para mobile/compact */}
      {(isMobile || isCompact) && matchStatus === 'FINISHED' && (
        <PlayerReviews
          player={player}
          currentUserId={currentUserId}
          isMobile={isMobile}
          isCompact={isCompact}
          onCreateReview={onCreateReview}
          onViewReviews={onViewReviews}
          hasReviewedPlayer={hasReviewedPlayer}
          canViewReviews={canViewReviews}
          loadingReviews={loadingReviews}
        />
      )}

      {/* Tooltip para jugadores no registrados */}
      {!player.userId && (
        <Tooltip
          id={`player-tooltip-${player.id}`}
          style={{
            backgroundColor: '#FF6B6B',
            color: '#FFFFFF',
            fontSize: '11px',
            fontWeight: 'bold',
            borderRadius: '6px',
            padding: '6px 10px',
            zIndex: 1000
          }}
        />
      )}
    </div>
  );
};
